<template>
  <input
    :id="id"
    ref="inputRef"
    v-model="isOpen"
    tabindex="-1"
    role="button"
    type="checkbox"
    class="modal-toggle"
    @input="onChange"
  />
  <div
    v-bind="$attrs"
    ref="modalRef"
    class="modal !ml-0"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="labelId"
    :aria-hidden="!isOpen"
  >
    <slot
      v-if="$slots.body"
      name="body"
      :close="close"
      :is-open="isOpen"
      :label-id="labelId"
    />
    <DialogBody v-else :is-open="isOpen" :label-id="labelId" @close="close">
      <template #title="props">
        <slot name="title" v-bind="props" />
      </template>

      <slot />
    </DialogBody>

    <button class="modal-backdrop" tabindex="-1" @click="close">
      {{ $t("common.modal.close") }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { getCurrentInstance, useTemplateRef, watchEffect } from "vue";
import DialogBody from "./dialog-body.vue";
import * as focusTrap from "focus-trap";
import { portalRootElementId } from "@/constants/ui";

const isOpen = defineModel<boolean>({
  required: true,
});
defineProps<{
  id?: string;
}>();

const modalRef = useTemplateRef("modalRef");
const instance = getCurrentInstance();
const labelId = `${instance?.uid ?? "modal"}-label`;

function close() {
  isOpen.value = false;
}

function onChange(e: Event) {
  const target = e.target as HTMLInputElement;
  isOpen.value = target.checked;
}

watchEffect((onCleanup) => {
  const element = modalRef.value;
  if (!element || !isOpen.value) return;

  // This throws error before we got to select community modal
  try {
    const portalsRoot = document.getElementById(portalRootElementId)!;
    // Enable focus on elements within the modal and to any teleported element
    // (e.g. dropdowns, combobox, select, etc.)
    const trap = focusTrap.createFocusTrap([element, portalsRoot], {
      escapeDeactivates: false, // We handle Escape ourselves to also close the modal
    });
    trap.activate();

    function onKeydown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
      }
    }
    document.addEventListener("keydown", onKeydown);

    onCleanup(() => {
      document.removeEventListener("keydown", onKeydown);
      trap.deactivate();
    });
  } catch (_error) {
    // Focus trap activation may fail silently
  }
});
</script>
